// Building helpers — operate on the active kingdom's slice of state.

import { Events, emit } from '../ui/eventBus.js'
import { BuildingConfig, BuildingBonusCap } from '../config/balance.js'
import { active, persist } from './state.js'

export function getBuildings() {
  return active().buildings ?? []
}

// Does the active kingdom own at least one building of this type?
export const hasBuildingType = (type) => getBuildings().some((b) => b.type === type)

export function isTileBuilt(tileIndex) {
  return (active().buildings ?? []).some((b) => b.tileIndex === tileIndex)
}

export function addBuilding(type, tileIndex, rotation = 0) {
  const k = active()
  if (!k.buildings) k.buildings = []
  // purchasedAt drives 15%/real-year Net Worth appreciation (CR-5). rotation = which of the tile's 6 facings it uses.
  k.buildings.push({ type, tileIndex, purchasedAt: Date.now(), rotation })
  emit(Events.BuildingAdded, { type, tileIndex, rotation })
  persist()
}

export const getBuilding = (tileIndex) => (active().buildings ?? []).find((b) => b.tileIndex === tileIndex) ?? null

// Take a building off its tile (a cancelled placement). Returns the removed record, or null.
export function removeBuilding(tileIndex) {
  const k = active()
  const i = (k.buildings ?? []).findIndex((b) => b.tileIndex === tileIndex)
  if (i < 0) return null
  const [b] = k.buildings.splice(i, 1)
  if (k.damagedTiles) delete k.damagedTiles[String(tileIndex)]
  persist()
  emit(Events.BuildingRemoved, { type: b.type, tileIndex })
  return b
}

export function setBuildingRotation(tileIndex, rotation) {
  const b = getBuilding(tileIndex)
  if (!b) return false
  b.rotation = ((rotation % 6) + 6) % 6
  persist()
  emit(Events.BuildingRotated, { tileIndex, rotation: b.rotation })
  return true
}

// Sum the relevant stat across every standing building (damaged ones
// contribute NOTHING so a torched building stops boosting the economy
// until the player repairs it), then clamp to the global cap defined in
// BuildingBonusCap. This is what keeps unlimited stacking from breaking
// the wealth curve — hospitals / schools / shops can stack, but the
// combined bonus tops out at the configured ceiling.
function buildingBonusSum(field) {
  const k = active()
  const damaged = k.damagedTiles ?? {}
  return (k.buildings ?? []).reduce((sum, b) => {
    if (damaged[String(b.tileIndex)]) return sum
    return sum + (BuildingConfig[b.type]?.[field] ?? 0)
  }, 0)
}

export function getYieldBonus() {
  return Math.min(BuildingBonusCap.yield, buildingBonusSum('yieldBonus'))
}

export function getGoonRateMultiplier() {
  return Math.min(BuildingBonusCap.goonRate, buildingBonusSum('goonRateMult'))
}
